import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useFormik } from 'formik';
import * as yup from 'yup';
import type { Player, Match } from '../../models';
import MatchAutocomplete from './MatchAutocomplete';

const roleOptions = [
  { value: 'BAT', label: 'BAT' },
  { value: 'BOWL', label: 'BOWL' },
  { value: 'ALL', label: 'ALL' },
  { value: 'WK', label: 'WK' },
];

const lineupStatusOptions = [
  { value: 'PLAYING', label: 'Playing' },
  { value: 'NOT_PLAYING', label: 'Not Playing' },
  { value: 'LAST_PLAYED', label: 'Last Played' },
  { value: 'UNAVAILABLE', label: 'Unavailable' },
  { value: 'X_FACTOR_SUBSTITUTE', label: 'X-Factor Substitute' },
  { value: 'USED_X_FACTOR_SUBSTITUTE', label: 'Used X-Factor Substitute' },
];

const validationSchema = yup.object({
  player: yup.object().shape({
    id: yup.number().required('Player is required'),
  }).required('Player is required'),
  match: yup.object().shape({
    id: yup.number().required('Match is required'),
  }).required('Match is required'),
  team: yup.object().shape({
    id: yup.number().required('Team is required'),
  }).nullable(),
  role: yup.string().required('Role is required'),
  lineup_status: yup.string().required('Lineup status is required'),
  credits: yup.number(),
  selected_perc: yup.number(),
  captain_perc: yup.number(),
  vice_captain_perc: yup.number(),
  series_points: yup.number(),
  series_avg_points: yup.number(),
  total_points: yup.number(),
});

export interface MatchPlayerFormValues {
  id: number;
  player: Player;
  match: number | null;
  team: any;
  role: string;
  lineup_status: string;
  credits: number;
  selected_perc: number;
  captain_perc: number;
  vice_captain_perc: number;
  series_points: number;
  series_avg_points: number;
  total_points: number;
}

interface MatchPlayerDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: MatchPlayerFormValues) => Promise<void>;
  initialValues: MatchPlayerFormValues;
  availablePlayers: Player[];
  mode: 'add' | 'edit';
}

const MatchPlayerDialog: React.FC<MatchPlayerDialogProps> = ({ open, onClose, onSubmit, initialValues, availablePlayers, mode }) => {
  const formik = useFormik<MatchPlayerFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>
          {mode === 'edit' ? 'Edit Match Player' : 'Add Match Player'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                name="player.id"
                label="Player"
                value={formik.values.player.id || ''}
                onChange={(e) => {
                  const selectedPlayer = availablePlayers.find(p => p.id === Number(e.target.value));
                  formik.setFieldValue('player', selectedPlayer || {});
                }}
                error={formik.touched.player?.id && Boolean(formik.errors.player?.id)}
                helperText={formik.touched.player?.id && formik.errors.player?.id}
              >
                {availablePlayers.map((player) => (
                  <MenuItem key={player.id} value={player.id}>
                    {player.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MatchAutocomplete
                value={formik.values.match ? { id: formik.values.match } as unknown as Match : null}
                onChange={(match) => {
                  formik.setFieldValue('match', match ? match.id : null);
                }}
                error={formik.touched.match && Boolean(formik.errors.match)}
                helperText={formik.touched.match && typeof formik.errors.match === 'string' ? formik.errors.match : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                name="role"
                label="Role"
                value={formik.values.role}
                onChange={formik.handleChange}
                error={formik.touched.role && Boolean(formik.errors.role)}
                helperText={formik.touched.role && formik.errors.role}
              >
                {roleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                name="lineup_status"
                label="Lineup Status"
                value={formik.values.lineup_status}
                onChange={formik.handleChange}
                error={formik.touched.lineup_status && Boolean(formik.errors.lineup_status)}
                helperText={formik.touched.lineup_status && formik.errors.lineup_status}
              >
                {lineupStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="credits"
                label="Credits"
                type="number"
                value={formik.values.credits}
                onChange={formik.handleChange}
                error={formik.touched.credits && Boolean(formik.errors.credits)}
                helperText={formik.touched.credits && formik.errors.credits}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="selected_perc"
                label="Selected %"
                type="number"
                value={formik.values.selected_perc}
                onChange={formik.handleChange}
                error={formik.touched.selected_perc && Boolean(formik.errors.selected_perc)}
                helperText={formik.touched.selected_perc && formik.errors.selected_perc}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="captain_perc"
                label="Captain %"
                type="number"
                value={formik.values.captain_perc}
                onChange={formik.handleChange}
                error={formik.touched.captain_perc && Boolean(formik.errors.captain_perc)}
                helperText={formik.touched.captain_perc && formik.errors.captain_perc}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="vice_captain_perc"
                label="Vice Captain %"
                type="number"
                value={formik.values.vice_captain_perc}
                onChange={formik.handleChange}
                error={formik.touched.vice_captain_perc && Boolean(formik.errors.vice_captain_perc)}
                helperText={formik.touched.vice_captain_perc && formik.errors.vice_captain_perc}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.lineup_status === 'PLAYING'}
                    onChange={(e) => {
                      formik.setFieldValue(
                        'lineup_status',
                        e.target.checked ? 'PLAYING' : 'NOT_PLAYING'
                      );
                    }}
                  />
                }
                label="Is Playing"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {mode === 'edit' ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MatchPlayerDialog; 